import { useState, useRef } from 'react';
import type { FC } from 'react';
import styled from 'styled-components';

import {
  registerIcon,
  defaultThemeProp,
  useAfterInitialEffect,
  useI18n,
  MenuButton,
  usePrevious
} from '@pega/cosmos-react-core';
import type { MenuItemProps } from '@pega/cosmos-react-core';
import type { RichTextEditorState } from '@pega/cosmos-react-rte';
import * as timesIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/times.icon';
import * as paperClipIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/paper-clip.icon';

import FeedVisibilityControls from './FeedVisibilityControls';
import type { FeedNewPostProps, Visibility, FeedInputRegionProps } from './Feed.types';
import FeedInputRegion from './FeedInputRegion';
import FeedCustomInputRegion from './FeedCustomInputRegion';

registerIcon(timesIcon, paperClipIcon);

export const StyledFeedNewPost = styled.div``;

StyledFeedNewPost.defaultProps = defaultThemeProp;

const StyledMenuButton = styled.button`
  width: fit-content;
`;

const FeedNewPost: FC<FeedNewPostProps> = (props: FeedNewPostProps) => {
  const {
    id: newPostId,
    banners,
    progress,
    onRewriteClick,
    defaultConfig,
    customConfig,
    postType,
    postTypes = [],
    visibilityMode = 'public',
    recipientInput,
    onPostTypeChange,
    ...restProps
  } = props;
  const t = useI18n();

  const rteRef = useRef<RichTextEditorState>(null);
  const [postTypeMenuItems, setPostTypeMenuItems] = useState<MenuItemProps[]>(
    postTypes.map(type => ({ ...type, primary: type.label, selected: type.id === postType?.id }))
  );
  const [visibility, setVisibility] = useState<Visibility>(
    visibilityMode === 'selectable' ? 'public' : visibilityMode
  );

  useAfterInitialEffect(() => {
    setVisibility(visibilityMode === 'selectable' ? 'public' : visibilityMode);
  }, [visibilityMode]);

  const previousPostType = usePrevious(postType);

  useAfterInitialEffect(() => {
    if (
      postType?.id !== previousPostType?.id &&
      !customConfig?.content &&
      !!defaultConfig &&
      rteRef.current
    ) {
      rteRef.current.focus();
    }

    setPostTypeMenuItems(
      postTypes.map(type => ({ ...type, primary: type.label, selected: type.id === postType?.id }))
    );
  }, [postType]);

  const inputProps: Pick<
    FeedInputRegionProps,
    | 'variant'
    | 'id'
    | 'banners'
    | 'progress'
    | 'onRewriteClick'
    | 'recipients'
    | 'postType'
    | 'postTypeMenu'
    | 'visibility'
    | 'visibilityControl'
  > = {
    variant: 'post',
    id: newPostId,
    banners,
    progress,
    onRewriteClick,
    recipients: recipientInput?.recipients ?? [],
    postType,
    postTypeMenu:
      postType && postTypes.length > 1 ? (
        <h3>
          <MenuButton
            as={StyledMenuButton}
            text={postType.label}
            variant='link'
            showArrow
            aria-label={`${t('feed_post_type')} - ${postType.label}`}
            menu={{
              mode: 'single-select',
              items: postTypeMenuItems,
              onItemClick: id => {
                const selected = postTypes.find(type => type.id === id);
                if (selected) {
                  onPostTypeChange?.(selected);
                }
              }
            }}
            onClick={e => {
              e.stopPropagation();
            }}
          />
        </h3>
      ) : undefined,
    visibility,
    visibilityControl:
      visibilityMode !== 'public' && !!recipientInput ? (
        <FeedVisibilityControls
          visibilityMode={visibilityMode}
          visibility={visibility}
          setVisibility={setVisibility}
          recipientInput={recipientInput}
        />
      ) : undefined
  };

  return (
    <StyledFeedNewPost {...restProps}>
      {(!!postTypes.length || !!postType) && !customConfig?.content && !!defaultConfig && (
        <FeedInputRegion {...inputProps} {...defaultConfig} rteRef={rteRef} />
      )}
      {(!!postTypes.length || !!postType) && !!customConfig?.content && (
        <FeedCustomInputRegion {...inputProps} {...customConfig} />
      )}
    </StyledFeedNewPost>
  );
};

export default FeedNewPost;
